import { CheckCircle, MoreVert, Save } from '@mui/icons-material';
import { Box, CircularProgress, Divider, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { Fragment, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Alert from '/@/components/bayer/alert';
import Icon from '/@/components/icon';
import useUnitProvider from '/@/core/context/unit';
import { useCornTechnologies } from '/@/hooks/catalog';
import { useMutateCreateOrUpdateCropMonitoring } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { useFieldById } from '/@/hooks/fields';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { useGetUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getSessionBrands, getSessionCountry } from '/@/store/session/selectors';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { CropMonitoringNote } from '/@/types/crop-monitoring-note';
import { formatDate } from '/@/utils';
import { getHybridProductName } from '/@/utils/crop-monitoring';
import type { CropMonitoringFormType } from '../../../creation-dialog';
import HybridBlock from '../../../creation-dialog/components/HybridBlock';
import { toFormHybrid } from '../../../creation-dialog/types';
import { getUserDisplayName, RegistryFooter, RegistryFooterActions, RegistryFooterRow } from './card-footer';
import { COLUMNS, Cell as GridCell } from './data-grid';
import { EditActions } from './edit-actions';
import { normalizeProductName } from './recommendation-helpers';
import { RegistryCard } from './registry-card';
import RegistryNotes from './registry-notes';
import { TablaCard, TablaCardRow } from './tabla-card';

/**
 * Every published brand codename for a crop, comma-joined.
 *
 * Both `/products/` and `/corn/technologies/` document a comma-separated brand list, and an
 * app instance can publish more than one brand per crop (DK and Preceon, say) — so passing
 * only the first silently hides everything the other brand offers.
 */
const brandCodenames = (brands?: { codename?: string }[]) =>
    (brands || [])
        .map((brand) => brand.codename)
        .filter(Boolean)
        .join(',');

const Cell = ({ title, value }: { title: string; value: string | number }) => {
    return (
        <Stack direction='column' spacing={0.5} sx={{ p: 1, backgroundColor: 'grey.100' }}>
            <Typography variant='caption' color='primary'>
                {title}
            </Typography>
            <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                {value}
            </Typography>
        </Stack>
    );
};

const buildFormDefaults = (cropMonitoring: CropMonitoring): CropMonitoringFormType => ({
    fieldId: cropMonitoring.field,
    id: cropMonitoring.id,
    crop: cropMonitoring.crop,
    previousCrop: cropMonitoring.previousCrop ?? '',
    hybrids: [toFormHybrid(cropMonitoring)],
});

const RegistryPlanting = ({
    cropMonitoring,
    notes,
    recommendedProductNames,
    expanded,
    onToggle,
}: {
    cropMonitoring: CropMonitoring;
    notes: CropMonitoringNote[];
    /** Normalized names of products recommended for the campaign — flags a followed reco. */
    recommendedProductNames?: Set<string>;
    /** Mobile-only collapse state, injected by `Registries`' accordion. */
    expanded?: boolean;
    onToggle?: () => void;
}) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [editing, setEditing] = useState(false);
    const form = useForm<CropMonitoringFormType>({
        defaultValues: buildFormDefaults(cropMonitoring),
    });
    const { fields: hybridFields } = useFieldArray({ control: form.control, name: 'hybrids' });

    const units = useUnitProvider();
    const country = useSelector(getSessionCountry);
    const sessionBrands = useSelector(getSessionBrands);

    const fieldQuery = useFieldById({ id: cropMonitoring.field }, { enabled: !!cropMonitoring.field });

    const technologiesQuery = useCornTechnologies({
        page: 1,
        pageSize: 10000,
        brand: brandCodenames(sessionBrands.corn),
        country: country?.code,
    });
    const technologyName = (technologiesQuery.data?.results as { id: number; name?: string }[] | undefined)?.find(
        (t) => t.id === cropMonitoring.cornCropMonitoring?.technology
    )?.name;

    const fromFieldview = !!cropMonitoring.sourcePlantingReport;
    const hasGenericProduct = !!cropMonitoring.cornCropMonitoring?.hasGenericProduct;

    const closeMenu = () => setMenuAnchor(null);

    // Multi-hybrid (CUL-1505, approach B): the Siembra registry shows one row per active hybrid
    // of the campaign, each its own CropMonitoring.
    const hybridCms = (fieldQuery.data?.activeCropMonitorings ?? []).filter(
        (cm) => cm.campaign === cropMonitoring.campaign
    );
    const hybridRows = hybridCms.length ? hybridCms : [cropMonitoring];
    const isMultiHybrid = hybridRows.length > 1;
    const techResults = (technologiesQuery.data?.results as { id: number; name?: string }[] | undefined) ?? [];
    const techName = (id?: number | null) => techResults.find((t) => t.id === id)?.name ?? '—';

    // V6 lock is lote-wide (CUL-1505): any hybrid reaching V6 freezes the registry.
    const reachedV6 = hybridRows.some((h) =>
        h.cornCropMonitoring?.stages?.some((stage) => (stage.vegetativeStage || 0) >= 6)
    );

    const startEditing = () => {
        closeMenu();
        form.reset({
            fieldId: cropMonitoring.field,
            id: cropMonitoring.id,
            crop: cropMonitoring.crop,
            previousCrop: cropMonitoring.previousCrop ?? '',
            hybrids: hybridRows.map(toFormHybrid),
        });
        setEditing(true);
    };

    const cancelEditing = () => setEditing(false);

    const hybrids = form.watch('hybrids');

    // Each hybrid is its own CropMonitoring (CUL-1505): edit them all at once, saved by id
    // through the bulk endpoint. HybridBlock renders each block and owns its own product/
    // technology queries, so no flat product/environment state is needed here.
    const { trigger: createOrUpdateCropMonitoring, isMutating } = useMutateCreateOrUpdateCropMonitoring(
        {
            hybrids,
            field: cropMonitoring.field,
            crop: cropMonitoring.crop || undefined,
            previousCrop: cropMonitoring.previousCrop || undefined,
            id: cropMonitoring.id,
        },
        {
            onSuccess: () => setEditing(false),
        }
    );

    const isValid =
        (hybrids ?? []).length > 0 &&
        (hybrids ?? []).every(
            (h) =>
                !!h.plantingDate &&
                !!h.environment &&
                h.product !== undefined &&
                h.product !== '' &&
                (cropMonitoring.crop !== 'corn' || !!h.technology)
        );

    const handleSave = async () => {
        if (!isValid) return;
        await createOrUpdateCropMonitoring();
    };

    const plantingDateDisplay = cropMonitoring.plantingDate ? formatDate(new Date(cropMonitoring.plantingDate)) : '—';
    const ambiente = cropMonitoring.environment
        ? units.toYield(cropMonitoring.environment, cropMonitoring.crop, 0).toString()
        : '—';
    const superficie =
        cropMonitoring.plantedArea != null
            ? units.toArea(cropMonitoring.plantedArea, 2).toString()
            : cropMonitoring.fieldArea != null
              ? convertAreaFromM2(Number(cropMonitoring.fieldArea), units).toString()
              : '—';
    const densidad =
        cropMonitoring.density != null
            ? units.toDensity(cropMonitoring.density, cropMonitoring.crop, 0, 'planting').toString()
            : '—';
    const registeredAt = cropMonitoring.created ? formatDate(new Date(cropMonitoring.created)) : null;
    const followedRecommendation = Boolean(
        cropMonitoring.productName && recommendedProductNames?.has(normalizeProductName(cropMonitoring.productName))
    );
    const userNodeQuery = useGetUserNode(cropMonitoring.createdBy ?? 0);
    const authorName = getUserDisplayName(userNodeQuery.data);

    // Mobile-only (desktop uses the ⋮ menu); plantings are edited, never deleted, from here.
    const footerActions =
        onToggle && expanded && !editing && !reachedV6 ? <RegistryFooterActions onEdit={startEditing} /> : undefined;

    return (
        <RegistryCard
            header={
                <>
                    {fromFieldview && (
                        <Icon type='fieldview' style={{ fill: '#F7B618', width: '20px', height: '20px' }} />
                    )}
                    <Typography variant='h6' color='primary'>
                        {_('Siembra')}
                    </Typography>
                </>
            }
            menu={
                // Mobile has an edit icon in the footer instead — the header only has room for the
                // expand chevron.
                onToggle ? undefined : (
                    <>
                        <IconButton
                            size='small'
                            aria-label={_('Más opciones')}
                            onClick={(e) => setMenuAnchor(e.currentTarget)}
                        >
                            <MoreVert fontSize='small' />
                        </IconButton>
                        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                            <Tooltip
                                arrow
                                title={reachedV6 ? _('Este registro no se puede editar, el cultivo alcanzó V6.') : ''}
                            >
                                <span>
                                    <MenuItem onClick={startEditing} disabled={editing || reachedV6}>
                                        {_('Editar')}
                                    </MenuItem>
                                </span>
                            </Tooltip>
                        </Menu>
                    </>
                )
            }
            footer={
                fromFieldview ? (
                    <RegistryFooterRow actions={footerActions}>
                        <Typography variant='caption' color='primary.mui.inactive'>
                            {_('Sincronizado desde FieldView')}
                        </Typography>
                    </RegistryFooterRow>
                ) : (
                    registeredAt && (
                        <RegistryFooter authorName={authorName} date={registeredAt} actions={footerActions} />
                    )
                )
            }
            expanded={expanded}
            onToggle={onToggle}
        >
            {hasGenericProduct && (
                <Alert severity='danger'>
                    {_(
                        'No pudimos identificar este híbrido dentro del modelo de GDUs. Los datos de fenología se calcularán con un modelo genérico.'
                    )}
                </Alert>
            )}

            {editing && fieldQuery.data ? (
                <Stack spacing={2}>
                    {hybridFields.map((hf, index) => (
                        <Stack key={hf.id} spacing={2} sx={{ width: '100%' }}>
                            {index > 0 && <Divider sx={{ width: '100%' }} />}
                            <HybridBlock
                                form={form}
                                index={index}
                                activeField={fieldQuery.data}
                                crop={cropMonitoring.crop}
                                canRemove={false}
                                onRemove={() => undefined}
                                onlyEdit
                                showHeader={isMultiHybrid}
                                lockReportFields={!!hybridRows[index]?.sourcePlantingReport}
                            />
                        </Stack>
                    ))}
                    <EditActions
                        onCancel={cancelEditing}
                        onSave={handleSave}
                        saveDisabled={!isValid || isMutating}
                        cancelDisabled={isMutating}
                        saveColor={onToggle ? 'primary' : 'secondary'}
                        saveStartIcon={isMutating ? <CircularProgress size={18} /> : <Save fontSize='small' />}
                    />
                </Stack>
            ) : isMultiHybrid ? (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(6, minmax(120px, 1fr))',
                        overflow: 'auto',
                        width: '100%',
                    }}
                >
                    <GridCell header>{_('Fecha de siembra')}</GridCell>
                    <GridCell header>{_('Producto')}</GridCell>
                    <GridCell header>{_('Tecnología')}</GridCell>
                    <GridCell header>{_('Ambiente')}</GridCell>
                    <GridCell header>{_('Superficie sembrada')}</GridCell>
                    <GridCell header>{_('Densidad')}</GridCell>
                    {hybridRows.map((h) => (
                        <Fragment key={h.id}>
                            <GridCell>{h.plantingDate ? formatDate(new Date(h.plantingDate)) : '—'}</GridCell>
                            <GridCell>{getHybridProductName(h) ?? '—'}</GridCell>
                            <GridCell>{techName(h.cornCropMonitoring?.technology)}</GridCell>
                            <GridCell>
                                {h.environment ? units.toYield(h.environment, cropMonitoring.crop, 0).toString() : '—'}
                            </GridCell>
                            <GridCell>
                                {h.plantedArea != null ? units.toArea(h.plantedArea, 2).toString() : '—'}
                            </GridCell>
                            <GridCell>
                                {h.density != null
                                    ? units.toDensity(h.density, cropMonitoring.crop, 0).toString()
                                    : '—'}
                            </GridCell>
                        </Fragment>
                    ))}
                </Box>
            ) : onToggle ? (
                <TablaCard>
                    <TablaCardRow label={_('Fecha de siembra')} value={plantingDateDisplay} />
                    <TablaCardRow label={_('Producto')} value={getHybridProductName(cropMonitoring) ?? '—'} />
                    <TablaCardRow label={_('Tecnología')} value={technologyName ?? '—'} />
                    <TablaCardRow label={_('Ambiente')} value={ambiente} />
                    <TablaCardRow label={_('Superficie sembrada')} value={superficie} />
                    <TablaCardRow label={_('Densidad')} value={densidad} />
                </TablaCard>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: COLUMNS, gap: 1, overflow: 'auto', width: '100%' }}>
                    <Cell title={_('Fecha de siembra')} value={plantingDateDisplay} />
                    <Cell title={_('Producto')} value={getHybridProductName(cropMonitoring) ?? '—'} />
                    <Cell title={_('Tecnología')} value={technologyName ?? '—'} />
                    <Cell title={_('Ambiente')} value={ambiente} />
                    <Cell title={_('Superficie sembrada')} value={superficie} />
                    <Cell title={_('Densidad')} value={densidad} />
                </Box>
            )}

            <RegistryNotes
                notes={notes}
                fieldId={cropMonitoring.field}
                plantingDateCropMonitoringId={cropMonitoring.id}
            />

            {followedRecommendation && (
                <Alert severity='blue' icon={<CheckCircle fontSize='inherit' />}>
                    {_('La siembra se realizó con el producto que se recomendó.')}
                </Alert>
            )}
        </RegistryCard>
    );
};

export default RegistryPlanting;
